import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { InterimLazyIcon, InterimNormalIcon, InterimStaleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { clone } from 'lodash-es';
import { isValidElement } from 'react';
import { StyledMark } from './styled';
import { NODE_DIMENSION, REACT_FLOW_CLASS, TILE_NODE_HEIGHT, TILE_NODE_WIDTH } from './tokens';
const NODE_OPERATION_TO_PORT_VISUAL = {
    created: 'created',
    updated: 'modified'
};
export function portHighlightKeyFromModification(modified, runtimeState) {
    if (modified !== undefined) {
        return NODE_OPERATION_TO_PORT_VISUAL[modified];
    }
    return runtimeState;
}
export function isElementInReactFlow(element) {
    return !!element.closest(`.${REACT_FLOW_CLASS}`);
}
export function isGraphPresent() {
    return Boolean(document.querySelector(`.${REACT_FLOW_CLASS}`));
}
export function getTileSize(_noOfSourceHandles = 0, _noOfTargetHandles = 0) {
    return { height: TILE_NODE_HEIGHT, width: TILE_NODE_WIDTH };
}
export function getSize(noOfSourceHandles = 0, noOfTargetHandles = 0, size) {
    const maxPorts = Math.max(noOfSourceHandles, noOfTargetHandles);
    let divider = 3.5;
    if (maxPorts >= 4) {
        size = (size * maxPorts) / divider;
    }
    return size;
}
export function stripNodeProperties(node) {
    const { data, ...restNode } = node;
    // strip all react elements and functions
    const cloneableData = Object.fromEntries(Object.entries(data).filter(([_key, value]) => {
        return !(isValidElement(value) || typeof value === 'function');
    }));
    const _node = clone(restNode);
    const _data = clone(cloneableData);
    _node.data = _data;
    return _node;
}
export function defaultFilterNodes(_node) {
    return true;
}
/**
 * Note the getRectOfNodes util from react-flow, doesn't give rect based on absolute position,
 * until its explicitly set.
 */
export function getNodeRect(node, nodeInternals) {
    const nodeInternal = nodeInternals.get(node.id);
    const position = nodeInternals.get(node.id)?.positionAbsolute || node.position;
    const width = nodeInternal?.width || node.width || NODE_DIMENSION;
    const height = nodeInternal?.height || node.height || NODE_DIMENSION;
    return {
        ...position,
        width,
        height
    };
}
export function getTextWithHighlight(text = '', searchQuery = '', style) {
    if (!searchQuery || searchQuery.trim().length === 0)
        return text;
    const matchIndex = text.toLowerCase().indexOf(searchQuery.toLowerCase());
    if (matchIndex !== -1) {
        return (_jsxs(_Fragment, { children: [text.substring(0, matchIndex), _jsx(StyledMark, { style: style, children: text.substring(matchIndex, matchIndex + searchQuery.length) }), text.substring(matchIndex + searchQuery.length)] }));
    }
    else {
        return text;
    }
}
export function getInterimIcon({ isStale, isLazyLoadInterim }) {
    const height = theme.sizes.x24;
    const width = theme.sizes.x24;
    if (isLazyLoadInterim)
        return _jsx(InterimLazyIcon, { height: height, width: width });
    if (isStale)
        return _jsx(InterimStaleIcon, { height: height, width: width });
    return _jsx(InterimNormalIcon, { height: height, width: width });
}
/**
 * Get the absolute position of a React Flow node
 * Falls back to regular position if positionAbsolute is not available
 *
 * @param node - React Flow Node instance
 * @returns Absolute position { x, y }
 */
export function getNodeAbsolutePosition(node) {
    return node.positionAbsolute ? { ...node.positionAbsolute } : { ...node.position };
}
export { getNodeSize } from './graphLayoutPrimitives';
export { getTargetHandleIndex, isNodeNotPositioned, isNotPositioned } from './graphLayoutPrimitives';
export { snapPosition } from './graphLayoutPrimitives';
export { checkRectangleOverlap, sortNodesByTopLeft } from './graphLayoutPrimitives';
export { recomputeAbsolutePositions } from './layoutUtils';
